"use client";
import { Button } from "@/app/components/shadcn/button";
import { X } from "lucide-react";
import Link from "next/link";
import useSearch from "@/app/lib/features/search/search.hook";
import PastSearchesSkeleton from "@/app/components/elements/skeletons/PastSearchesSkeleton";
import { toast } from "sonner";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface PastSearchesProps {
  onClose: () => void;
}

const PastSearches = ({ onClose }: PastSearchesProps) => {
  const {
    pastSearches,
    isLoading,
    clearHistory,
    deleteSingleSearch,
    isClearing,
    isDeleting,
  } = useSearch();

  const handleClear = async () => {
    trackGeneric("search_history_clear", "click");
    const success = await clearHistory();
    toast[success ? "success" : "error"](
      success ? "Arama geçmişi temizlendi" : "Arama geçmişi temizlenemedi",
    );
  };

  const handleDelete = async (id: number, term: string) => {
    trackGeneric("search_history_delete", "click");
    const success = await deleteSingleSearch(id);
    toast[success ? "success" : "error"](
      success ? `"${term}" silindi` : "Arama silinemedi",
    );
  };

  if (isLoading) return <PastSearchesSkeleton />;
  if (!pastSearches || pastSearches.items.length === 0) return null;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-sm sm:text-base">
          {pastSearches.title}
        </h2>
        {pastSearches.top_button && (
          <Button
            variant="link"
            className="p-0 text-xs sm:text-sm"
            onClick={handleClear}
            disabled={isClearing}
          >
            {isClearing ? "Temizleniyor..." : pastSearches.top_button}
          </Button>
        )}
      </div>
      <div className="space-y-0.5 max-h-32 sm:max-h-40 overflow-y-auto">
        {pastSearches.items.map((search) => (
          <div key={search.id} className="relative group/past">
            <Button
              variant="ghost"
              asChild
              className="w-full font-normal justify-start pr-8 text-sm h-8 sm:h-10"
            >
              <Link
                href={`/aramasonucu?q=${encodeURIComponent(search.search_term)}`}
                onClick={() => { trackGeneric("search_past", "click"); onClose(); }}
              >
                {search.search_term}
              </Link>
            </Button>
            <button
              type="button"
              onClick={() => handleDelete(search.id, search.search_term)}
              disabled={isDeleting}
              className="absolute cursor-pointer right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <X size={12} className="sm:hidden" />
              <X size={14} className="hidden sm:block" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PastSearches;
